// src/components/FileUploader.tsx

import React from 'react'
import { useDropzone, DropzoneOptions } from 'react-dropzone'
import { Spinner } from 'react-bootstrap'
import { CloudUpload } from 'lucide-react'
import styled from 'styled-components'

interface FileUploaderProps {
  onFileSelect: (files: File[]) => void
  uploading?: boolean
  accept?: DropzoneOptions['accept']
  maxSize?: number
  forModel?: boolean
  numFiles?: number
}

const DropzoneContainer = styled.div`
  width: 100%;
  padding: 1.5rem;
`

const DropArea = styled.div`
  background: #212529;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
`

const DropTarget = styled.div<{ $isDragActive: boolean }>`
  border: 2.5px dashed ${(props) => (props.$isDragActive ? '#0dcaf0' : '#495057')};
  border-radius: 1rem;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background: ${(props) => (props.$isDragActive ? 'rgba(13, 202, 240, 0.1)' : '#2b3035')};

  &:hover {
    border-color: #0dcaf0;
    background: rgba(13, 202, 240, 0.1);
  }

  .upload-icon {
    color: ${(props) => (props.$isDragActive ? '#0dcaf0' : '#adb5bd')};
    margin-bottom: 1.5rem;
    transition: color 0.2s ease-in-out;
  }

  .spinner-container {
    margin-bottom: 1.5rem;

    .spinner-border {
      color: #0dcaf0;
      width: 3rem;
      height: 3rem;
    }
  }

  .text-primary {
    color: #f8f9fa;
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 0.75rem;
  }

  .text-secondary {
    color: #adb5bd;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  .files-selected {
    display: inline-block;
    margin-top: 1.5rem;
    padding: 0.5rem 1rem;
    background: rgba(13, 202, 240, 0.15);
    color: #0dcaf0;
    border-radius: 0.5rem;
    font-weight: 500;
    font-size: 0.875rem;
  }
`

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelect,
  uploading = false,
  accept = {
    'image/tiff': ['.tiff', '.tif', '.TIFF', '.TIF'],
    'image/jpeg': ['.jpg', '.jpeg', '.JPEG', '.JPG'],
    'image/png': ['.png', '.PNG'],
  },
  maxSize = 5 * 1024 * 1024 * 1024, // 5 GB
  forModel = false,
  numFiles = 0,
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onFileSelect,
    accept,
    maxSize,
  })

  return (
    <DropzoneContainer>
      <DropArea>
        <DropTarget {...getRootProps()} $isDragActive={isDragActive}>
          <input {...getInputProps()} multiple />
          {uploading ? (
            <div className="spinner-container">
              <Spinner animation="border" variant="info" />
            </div>
          ) : (
            <CloudUpload className="upload-icon" size={64} />
          )}
          <p className="text-primary">
            {uploading
              ? 'Uploading...'
              : isDragActive
              ? 'Drop the files here'
              : 'Click or drag and drop files'}
          </p>
          {!uploading && !forModel && (
            <>
              <p className="text-secondary">Supported formats: TIFF, JPG, JPEG, PNG</p>
              <p className="text-secondary">Maximum file size: 5 GB</p>
            </>
          )}
          {numFiles > 0 && (
            <div className="files-selected">
              {numFiles} file{numFiles !== 1 ? 's' : ''} selected
            </div>
          )}
        </DropTarget>
      </DropArea>
    </DropzoneContainer>
  )
}

export default FileUploader
